import { createFileRoute, redirect } from "@tanstack/react-router";

import { graphql } from "@repo/graphql-types";

import { fetchQuery } from "@/hooks/use-graphql";
import type { RootRouteContext } from "@/routes/-root/-root-context";

import type { JSX } from "react";

const ValidateRecipientDataQuery = graphql(/* GraphQL */ `
  query ValidateRecipientDataQueryIndex(
    $codUsuario: Int!
    $codClinica: Int
    $skipClinica: Boolean!
  ) {
    ExpensumGetRecipient(arg1: { codUsuario: $codUsuario, codClinica: $codClinica }) {
      recipient {
        id
      }
    }
    usuarios(where: { codUsuario: { _eq: $codUsuario } }) {
      codUsuario
      nome
      sobrenome
      cpf
      email
      dataNascimento
    }
    usuariosProfissionais(where: { codUsuario: { _eq: $codUsuario } }) {
      celularPessoalMembro
      profissao
    }
    usuariosEnderecos: tbUsuariosEnderecos(
      where: { codUsuario: { _eq: $codUsuario } }
      limit: 1
    ) {
      logradouro
      numero
      bairro
      cidade
      uf
      cep
    }
    clinicas: tbClinicas(where: { codClinica: { _eq: $codClinica } })
      @skip(if: $skipClinica) {
      codClinica
      razaoSocial
      cnpj
    }
    clinicasEnderecos: tbClinicasEnderecos(
      where: { codClinica: { _eq: $codClinica } }
      limit: 1
    ) @skip(if: $skipClinica) {
      logradouro
      numero
      bairro
      cidade
      uf
      cep
    }
  }
`);

const CreateRecipientPage = (): JSX.Element => {
  return <div>Hello /settings/payments/create-recipient/!</div>;
};

export const Route = createFileRoute("/settings/payments/create-recipient/")({
  component: CreateRecipientPage,
  beforeLoad: async ({ context, search }) => {
    const { user } = context;

    // Validar se os dados estão completos antes de redirecionar
    const hasRequiredData = await validateUserData(user, context);

    if (!hasRequiredData) {
      // Dados faltando: redirecionar para tela de validação
      redirect({
        to: "/settings/payments/create-recipient/validate-data",
        throw: true,
        search,
      });
    } else {
      // Dados completos: redirecionar baseado em PF/PJ
      const isCompany = Boolean(user.codClinica);

      if (isCompany) {
        // Usuário PJ: vai direto para confirmar documento
        redirect({
          to: "/settings/payments/create-recipient/confirm-document",
          throw: true,
          search,
        });
      } else {
        // Usuário PF: vai escolher tipo de titular
        redirect({
          to: "/settings/payments/create-recipient/choose-holder-type",
          throw: true,
          search,
        });
      }
    }
  },
});

async function validateUserData(
  user: { codUsuario: number; codClinica?: number | null },
  context: RootRouteContext,
): Promise<boolean> {
  try {
    const data = await fetchQuery(context, ValidateRecipientDataQuery, {
      codUsuario: user.codUsuario,
      codClinica: user.codClinica ?? undefined,
      skipClinica: !user.codClinica,
    });

    if (data.ExpensumGetRecipient?.recipient?.id) {
      return true;
    }

    const usuario = data.usuarios[0];
    const profissional = data.usuariosProfissionais[0];
    const enderecoUsuario = data.usuariosEnderecos[0];
    const clinica = data.clinicas?.[0];
    const enderecoClinica = data.clinicasEnderecos?.[0];

    const isCompany = Boolean(user.codClinica);

    if (!usuario.dataNascimento || !usuario.email) {
      return false;
    }

    if (!profissional.celularPessoalMembro || !profissional.profissao) {
      return false;
    }

    const endereco = isCompany ? enderecoClinica : enderecoUsuario;
    if (!endereco) {
      return false;
    }

    if (isCompany && (!clinica?.cnpj || !clinica.razaoSocial)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}
